import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import ExcelJS from 'exceljs';
import useConfig from 'hooks/useConfig';
import { useEffect, useState } from 'react';
import Charts, { Props } from 'react-apexcharts';
import { useIntl } from 'react-intl';
import { gridSpacing } from 'store/constant';
import SkeletonEarningCard from 'ui-component/cards/Skeleton/EarningCard';
import MainCard from '../components/cards/MainCard';

const baseChartData: Props = {
  height: '100%',
  type: 'bar',

  options: {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: {
        show: true,
        tools: {
          download: false
        }
      },
      zoom: {
        enabled: true
      }
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: 'bottom',
            offsetX: -10,
            offsetY: 0
          }
        }
      }
    ],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%'
      }
    },
    xaxis: {
      categories: []
    },
    yaxis: {
      title: {
        text: '',
        style: {
          fontSize: '14px',
          fontWeight: 'Normal',
          fontFamily: `'Roboto', sans-serif`,
          color: '#263238'
        }
      }
    },
    legend: {
      show: false,
      fontFamily: `'Roboto', sans-serif`,
      position: 'bottom',
      offsetX: 20,
      labels: {
        useSeriesColors: false
      },
      itemMargin: {
        horizontal: 15,
        vertical: 8
      }
    },
    fill: {
      type: 'solid'
    },
    dataLabels: {
      enabled: false
    },
    grid: {
      show: true
    },
    tooltip: {
      marker: {
        show: false
      }
    }
  },
  series: []
};

const generateColors = (count: number): string[] => {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    let color = '#' + Math.floor(Math.random() * 16777215).toString(16);
    while (color.length < 7) {
      color += '0';
    }
    colors.push(color);
  }
  return colors;
};

interface ChartProps {
  isLoading: boolean;
  labels: string[];
  data: any;
  title: string;
  isSessionStatus?: boolean;
  textCenter: string;
  setFilter: any;
  filterLabel?: string;
  type: any;
  url?: string;
  dateRange: any;
  filteredProviders: any;
}

const formatLocationType = (locationType, intl) => {
  switch (locationType) {
    case 'online':
      return intl.formatMessage({ defaultMessage: 'Virtual' });
    case 'office':
      return intl.formatMessage({ defaultMessage: 'In Person' });
    case 'text':
      return intl.formatMessage({ defaultMessage: 'Text' });
    case 'phone':
      return intl.formatMessage({ defaultMessage: 'Phone' });
    default:
      return locationType.charAt(0).toUpperCase() + locationType.slice(1);
  }
};

const generateTooltipContent = (providerData, locationTypes, selectedLabel, intl) => {
  const locationStats = providerData.locationStats;
  const totalCount = Object.values(locationStats).reduce((total, stats: any) => {
    return total + (stats[selectedLabel] || 0);
  }, 0);

  let tooltipContent = `<strong>${providerData.provider}</strong>: ${totalCount}<br/><br/>`;

  locationTypes.forEach((locationType) => {
    const stats = locationStats[locationType] || {
      accepted: 0,
      cancelled: 0,
      attended: 0,
      noshow: 0,
      lateCancelled: 0,
      pending: 0
    };

    const count = stats[selectedLabel] || 0;
    tooltipContent += `${formatLocationType(locationType, intl)}: ${count}<br/>`;
  });

  return tooltipContent;
};

const getLocalizedLabel = (label, intl) => {
  switch (label.toLowerCase()) {
    case 'accepted':
      return intl.formatMessage({ defaultMessage: 'Accepted' });
    case 'attended':
      return intl.formatMessage({ defaultMessage: 'Attended' });
    case 'cancelled':
      return intl.formatMessage({ defaultMessage: 'Cancelled' });
    case 'noshow':
      return intl.formatMessage({ defaultMessage: 'No Show' });
    case 'pending':
      return intl.formatMessage({ defaultMessage: 'Pending' });
    default:
      return label;
  }
};

const generateChartData = (sessionStatus, intl) => {
  const labels = ['accepted', 'attended', 'cancelled', 'noshow', 'pending'];
  const locationTypes = ['online', 'office', 'text', 'phone'];

  const filteredData = sessionStatus.filter((d) => d.accepted || d.attended || d.cancelled || d.noshow || d.pending);

  const data = filteredData.map((d, index) => ({
    name: d.provider,
    data: [d.accepted, d.attended, d.cancelled, d.noshow, d.pending]
  }));

  const colors = generateColors(filteredData.length);

  const tooltipFormatter = ({ seriesIndex, dataPointIndex }) => {
    const providerData = filteredData[seriesIndex];
    const selectedLabel = labels[dataPointIndex];
    return generateTooltipContent(providerData, locationTypes, selectedLabel, intl);
  };

  return {
    ...baseChartData,
    options: {
      ...baseChartData.options,
      xaxis: {
        categories: labels,
        labels: {
          formatter: function (value) {
            return getLocalizedLabel(value, intl);
          }
        }
      },
      yaxis: {
        title: {
          text: intl.formatMessage({ defaultMessage: 'Session Count' }),
          style: {
            fontSize: '14px',
            fontWeight: 'Normal',
            fontFamily: `'Roboto', sans-serif`,
            color: '#263238'
          }
        }
      },
      colors,
      tooltip: {
        y: {
          formatter: function (val, { seriesIndex, dataPointIndex }) {
            return tooltipFormatter({ seriesIndex, dataPointIndex });
          },
          title: {
            formatter: function (seriesName) {
              return '';
            }
          }
        }
      }
    },
    series: data
  };
};

const downloadXLSX = async (data: any, dateRange: any, intl: any) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Session Data');

  const labels = ['accepted', 'attended', 'cancelled', 'noshow', 'lateCancelled', 'pending'];
  const locationTypes = ['online', 'office', 'text', 'phone'];
  const locationLabels = {
    online: intl.formatMessage({ defaultMessage: 'Virtual' }),
    office: intl.formatMessage({ defaultMessage: 'In Person' }),
    text: intl.formatMessage({ defaultMessage: 'Text' }),
    phone: intl.formatMessage({ defaultMessage: 'Phone' })
  };

  const labelMessages = {
    accepted: intl.formatMessage({ defaultMessage: 'Accepted' }),
    attended: intl.formatMessage({ defaultMessage: 'Attended' }),
    cancelled: intl.formatMessage({ defaultMessage: 'Cancelled' }),
    noshow: intl.formatMessage({ defaultMessage: 'No Show' }),
    lateCancelled: intl.formatMessage({ defaultMessage: 'Late Cancelled' }),
    pending: intl.formatMessage({ defaultMessage: 'Pending' })
  };

  const headerRow = [
    intl.formatMessage({ defaultMessage: 'Provider/Provider-Admin' }),
    ...labels.flatMap((label) => [`${labelMessages[label]}`, '', '', ''])
  ];
  worksheet.addRow(headerRow);

  labels.forEach((_, index) => {
    const start = 2 + index * 4;
    const end = start + 3;
    worksheet.mergeCells(1, start, 1, end);
  });
  const subHeaderRow = [''];

  labels.forEach(() => {
    locationTypes.forEach((type) => {
      subHeaderRow.push(locationLabels[type]);
    });
  });
  worksheet.addRow(subHeaderRow);

  worksheet.getRow(1).eachCell((cell) => {
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.font = { bold: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFF00' }
    };
  });

  worksheet.getRow(2).eachCell((cell) => {
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.font = { bold: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'E0E0E0' }
    };
  });

  const totals: { [key: string]: number } = {};

  data.forEach((providerData: any) => {
    if (providerData.provider) {
      const row: any[] = [providerData.provider];

      labels.forEach((label) => {
        Object.keys(locationLabels).forEach((type) => {
          const locationStats = providerData.locationStats[type] || {};
          const count = locationStats[label] || 0;

          const totalKey = `${label}_${type}`;
          totals[totalKey] = (totals[totalKey] || 0) + count;

          row.push(count);
        });
      });

      worksheet.addRow(row);
    }
  });

  worksheet.addRow([]);

  const totalRow: any[] = [intl.formatMessage({ defaultMessage: 'Total' })];

  labels.forEach((label) => {
    Object.keys(locationLabels).forEach((type) => {
      const totalKey = `${label}_${type}`;
      const total = totals[totalKey] || 0;
      totalRow.push(total);
    });
  });
  const totalRowIndex = worksheet.addRow(totalRow).number;

  worksheet.getRow(totalRowIndex).eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD700' }
    };
    cell.border = {
      top: { style: 'thin' },
      bottom: { style: 'double' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };
  });
  worksheet.addRow(totalRowIndex);

  const fileName = `${intl.formatMessage({ defaultMessage: 'session_data' })}_${new Date(dateRange.startDate).toLocaleDateString(
    'en-CA'
  )}_${new Date(dateRange.endDate).toLocaleDateString('en-CA')}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;' });

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ChartCard = ({
  isLoading,
  title,
  textCenter: text,
  labels,
  data,
  isSessionStatus = false,
  url,
  filterLabel = 'Filter',
  dateRange,
  filteredProviders
}: ChartProps) => {
  const [chartData, setChartData] = useState(baseChartData);
  const { rtlLayout } = useConfig();
  const intl = useIntl();

  useEffect(() => {
    if (!isLoading && data) {
      const newChartData = generateChartData(data, intl);
      setChartData(newChartData);
    }
  }, [isLoading, data, intl]);

  return (
    <MainCard>
      {isLoading ? (
        <Box sx={{ height: 600, maxHeight: '100%' }}>
          <SkeletonEarningCard />
        </Box>
      ) : (
        <Box sx={{ height: 625, maxHeight: '100%' }}>
          <Grid container spacing={gridSpacing}>
            <Grid item xs={12}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                  <Grid container direction="column" spacing={1}>
                    <Grid item>
                      <Grid container alignItems="center">
                        <Typography variant="h3">{intl.formatMessage({ defaultMessage: 'Session Status' })}</Typography>
                        <Tooltip title={intl.formatMessage({ defaultMessage: 'Download CSV File' })}>
                          <IconButton
                            onClick={() => downloadXLSX(data, dateRange, intl)}
                            aria-label="download csv"
                            size="small"
                            sx={{ ml: 2 }}
                          >
                            <FileDownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ '& .apexcharts-legend-text': { marginLeft: rtlLayout ? '8px' : 'initial' } }}>
              <Charts options={chartData.options} series={chartData.series} type="bar" height={500} />
            </Grid>
          </Grid>
        </Box>
      )}
    </MainCard>
  );
};

export default ChartCard;
